/**
 * Palm Oracle API Service
 * Handles all API calls to Palm Oracle backend
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Custom error class for Palm API errors
 */
class PalmApiError extends Error {
  constructor({ status, code, message, responsePreview }) {
    super(message);
    this.name = 'PalmApiError';
    this.status = status;
    this.code = code;
    this.responsePreview = responsePreview;
  }
}

/**
 * Safe JSON parse with content-type check
 * Prevents "Unexpected token '<'" errors when server returns HTML
 * Properly handles 5xx server errors vs image quality errors
 */
const safeParseJson = async (response, operation = 'API call') => {
  const contentType = response.headers.get('content-type') || '';
  const status = response.status;
  
  // Server errors (5xx) or proxy errors (520, 521, 522, etc.)
  // These are NOT image quality issues - don't mislead user
  if (status >= 500 || status === 520 || status === 521 || status === 522 || status === 523 || status === 524) {
    const text = contentType.includes('application/json') 
      ? JSON.stringify(await response.json()) 
      : await response.text();
    
    throw new PalmApiError({
      status: status,
      code: 'SERVER_ERROR',
      message: 'Sunucu geçici olarak yanıt veremiyor. Lütfen birkaç saniye bekleyip tekrar deneyin.',
      responsePreview: text.slice(0, 200)
    });
  }
  
  // Check content type for non-5xx responses
  if (contentType.includes('application/json')) {
    return response.json();
  }
  
  // Server returned non-JSON (likely HTML error page)
  const text = await response.text();
  throw new PalmApiError({
    status: status,
    code: 'NON_JSON_SERVER_RESPONSE',
    message: `Sunucu geçersiz yanıt verdi (${operation}). Lütfen tekrar deneyin.`,
    responsePreview: text.slice(0, 200)
  });
};

/**
 * Get authorization header
 */
const getAuthHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

/**
 * Check if user has access to Palm Oracle
 */
export const checkPalmAccess = async (token) => {
  const response = await fetch(`${API_URL}/api/palm/access`, {
    headers: getAuthHeaders(token)
  });
  
  if (!response.ok) {
    throw new PalmApiError({
      status: response.status,
      code: 'ACCESS_CHECK_FAILED',
      message: 'Palm Oracle erişim kontrolü başarısız.'
    });
  }
  
  return safeParseJson(response, 'erişim kontrolü');
};

/**
 * Get Palm Oracle configuration
 */
export const getPalmConfig = async () => {
  const response = await fetch(`${API_URL}/api/palm/config`);
  
  if (!response.ok) {
    throw new PalmApiError({
      status: response.status,
      code: 'CONFIG_FAILED',
      message: 'Yapılandırma yüklenemedi.'
    });
  }
  
  return safeParseJson(response, 'yapılandırma');
};

/**
 * Get translations for Palm Oracle UI
 */
export const getPalmTranslations = async (lang = 'tr') => {
  const response = await fetch(`${API_URL}/api/palm/translations/${lang}`);
  
  if (!response.ok) {
    throw new PalmApiError({
      status: response.status,
      code: 'TRANSLATIONS_FAILED',
      message: 'Çeviriler yüklenemedi.'
    });
  }
  
  return safeParseJson(response, 'çeviriler');
};

/**
 * Create a new palm reading session
 */
export const createReading = async (token, data) => {
  const response = await fetch(`${API_URL}/api/palm/readings`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({
      package: data.package || 'standard',
      hand_selection: data.handSelection || 'right',
      dominant_hand: data.dominantHand || 'unknown',
      intent: data.intent || 'general',
      question: data.question || null,
      privacy_consent: data.privacyConsent ?? true,
      oracle_fusion_consent: data.oracleFusionConsent ?? false,
      idempotency_key: data.idempotencyKey || `palm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    })
  });
  
  if (!response.ok) {
    const errorData = await safeParseJson(response, 'okuma oluşturma');
    throw new PalmApiError({
      status: response.status,
      code: errorData.code || 'CREATE_READING_FAILED',
      message: errorData.detail || errorData.message || 'Okuma oluşturulamadı. Lütfen tekrar deneyin.'
    });
  }
  
  return safeParseJson(response, 'okuma oluşturma');
};

/**
 * Upload palm image for analysis
 */
export const uploadPalmImage = async (token, readingId, imageBase64, handSide = 'right') => {
  // Remove data URL prefix if present
  const cleanBase64 = imageBase64.replace(/^data:image\/\w+;base64,/, '');
  
  const response = await fetch(`${API_URL}/api/palm/readings/${readingId}/images`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({
      reading_id: readingId,
      hand_side: handSide,
      image_base64: cleanBase64
    })
  });
  
  if (!response.ok) {
    // Safe parse - handle non-JSON responses (520, HTML error pages)
    const errorData = await safeParseJson(response, 'fotoğraf yükleme');
    throw new PalmApiError({
      status: response.status,
      code: errorData.code || 'UPLOAD_FAILED',
      message: errorData.detail || errorData.message || 'Fotoğraf yüklenemedi. Lütfen tekrar deneyin.'
    });
  }
  
  return safeParseJson(response, 'fotoğraf yükleme');
};

/**
 * Get reading status (for polling)
 */
export const getReadingStatus = async (token, readingId) => {
  const response = await fetch(`${API_URL}/api/palm/readings/${readingId}/status`, {
    headers: getAuthHeaders(token)
  });
  
  if (!response.ok) {
    const errorData = await safeParseJson(response, 'durum kontrolü');
    throw new PalmApiError({
      status: response.status,
      code: errorData.code || 'STATUS_CHECK_FAILED',
      message: errorData.detail || 'Okuma durumu alınamadı.'
    });
  }
  
  return safeParseJson(response, 'durum kontrolü');
};

/**
 * Get complete reading result
 */
export const getReadingResult = async (token, readingId) => {
  const response = await fetch(`${API_URL}/api/palm/readings/${readingId}`, {
    headers: getAuthHeaders(token)
  });
  
  if (!response.ok) {
    const errorData = await safeParseJson(response, 'okuma sonucu');
    throw new PalmApiError({
      status: response.status,
      code: errorData.code || 'GET_RESULT_FAILED',
      message: errorData.detail || 'Okuma sonucu alınamadı.'
    });
  }
  
  return safeParseJson(response, 'okuma sonucu');
};

/**
 * List user's palm readings
 */
export const listReadings = async (token, limit = 10, offset = 0) => {
  const response = await fetch(`${API_URL}/api/palm/readings?limit=${limit}&offset=${offset}`, {
    headers: getAuthHeaders(token)
  });
  
  if (!response.ok) {
    const errorData = await safeParseJson(response, 'okuma listesi');
    throw new PalmApiError({
      status: response.status,
      code: errorData.code || 'LIST_FAILED',
      message: errorData.detail || 'Okumalar listelenemedi.'
    });
  }
  
  return safeParseJson(response, 'okuma listesi');
};

/**
 * Delete a palm reading
 */
export const deleteReading = async (token, readingId, deleteImages = true, deleteAnalysis = true) => {
  const response = await fetch(`${API_URL}/api/palm/readings/${readingId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token),
    body: JSON.stringify({
      delete_images: deleteImages,
      delete_analysis: deleteAnalysis
    })
  });
  
  if (!response.ok) {
    const errorData = await safeParseJson(response, 'okuma silme');
    throw new PalmApiError({
      status: response.status,
      code: errorData.code || 'DELETE_FAILED',
      message: errorData.detail || 'Okuma silinemedi.'
    });
  }
  
  return safeParseJson(response, 'okuma silme');
};

/**
 * Poll for reading completion
 * Returns a promise that resolves when reading is complete or fails
 */
export const pollReadingStatus = async (token, readingId, onProgress, maxAttempts = 60, intervalMs = 2000) => {
  let attempts = 0;
  
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const status = await getReadingStatus(token, readingId);
        
        // Call progress callback
        if (onProgress) {
          onProgress(status);
        }
        
        // Check if completed or failed
        if (status.status === 'completed') {
          resolve(status);
          return;
        }
        
        if (['failed', 'quality_rejected', 'refunded'].includes(status.status)) {
          reject(new Error(status.error_message || 'Reading failed'));
          return;
        }
        
        // Continue polling
        attempts++;
        if (attempts >= maxAttempts) {
          reject(new Error('Reading timed out'));
          return;
        }
        
        setTimeout(poll, intervalMs);
      } catch (error) {
        reject(error);
      }
    };
    
    poll();
  });
};

// Hand selection options
export const HAND_OPTIONS = [
  { value: 'left', labelTr: 'Sol El', labelEn: 'Left Hand', icon: '🤚' },
  { value: 'right', labelTr: 'Sağ El', labelEn: 'Right Hand', icon: '✋' },
];

// Dominant hand options  
export const DOMINANT_HAND_OPTIONS = [
  { value: 'left', labelTr: 'Sol', labelEn: 'Left' },
  { value: 'right', labelTr: 'Sağ', labelEn: 'Right' },
  { value: 'unknown', labelTr: 'Bilmiyorum', labelEn: 'Unknown' },
];

// Intent options
export const INTENT_OPTIONS = [
  { value: 'general', labelTr: 'Genel Okuma', labelEn: 'General Reading', icon: '🔮' },
  { value: 'love', labelTr: 'Aşk & İlişkiler', labelEn: 'Love & Relationships', icon: '❤️' },
  { value: 'career', labelTr: 'Kariyer & İş', labelEn: 'Career & Work', icon: '💼' },
  { value: 'money', labelTr: 'Para & Finans', labelEn: 'Money & Finance', icon: '💰' },
  { value: 'growth', labelTr: 'Kişisel Gelişim', labelEn: 'Personal Growth', icon: '🌱' },
  { value: 'family', labelTr: 'Aile & Sağlık', labelEn: 'Family & Health', icon: '👨‍👩‍👧' },
  { value: 'just_read', labelTr: 'Sadece Oku', labelEn: 'Just Read', icon: '👁️' },
];

// Package options
export const PACKAGE_OPTIONS = [
  { 
    value: 'standard', 
    labelTr: 'Standart Okuma', 
    labelEn: 'Standard Reading',
    descTr: 'Ana çizgiler + Temel Palm DNA',
    descEn: 'Main lines + Basic Palm DNA',
    cost: 5
  },
  { 
    value: 'deep', 
    labelTr: 'Derin Analiz', 
    labelEn: 'Deep Analysis',
    descTr: 'Tüm çizgiler + Detaylı Palm DNA + AI Yorumu',
    descEn: 'All lines + Detailed Palm DNA + AI Interpretation',
    cost: 8
  },
  { 
    value: 'oracle_fusion', 
    labelTr: 'Oracle Fusion', 
    labelEn: 'Oracle Fusion',
    descTr: 'Derin Analiz + Tarot/Kahve entegrasyonu',
    descEn: 'Deep Analysis + Tarot/Coffee integration',
    cost: 12,
    premium: true
  },
];

// Reading status labels
export const STATUS_LABELS = {
  created: { tr: 'Oluşturuldu', en: 'Created' },
  uploading: { tr: 'Yükleniyor', en: 'Uploading' },
  validating: { tr: 'Kontrol Ediliyor', en: 'Validating' },
  quality_rejected: { tr: 'Kalite Yetersiz', en: 'Quality Rejected' },
  analyzing: { tr: 'Analiz Ediliyor', en: 'Analyzing' },
  interpreting: { tr: 'Yorumlanıyor', en: 'Interpreting' },
  completed: { tr: 'Tamamlandı', en: 'Completed' },
  failed: { tr: 'Başarısız', en: 'Failed' },
  refunded: { tr: 'İade Edildi', en: 'Refunded' }
};


// Export error class for catching specific errors
export { PalmApiError };
